import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { RecentEntity, EntityType, MAX_RECENT_ENTITIES } from '../types/RecentEntities';
import {
  ClientUserInterface,
  ClientBelief,
  ClientThoughtWithAuthor,
  ClientFeed,
} from '../Admin-ClientLibrary';

interface RecentEntitiesContextType {
  recentEntities: RecentEntity[];
  addRecentEntity: (
    type: EntityType,
    data: ClientUserInterface | ClientBelief | ClientThoughtWithAuthor | ClientFeed
  ) => void;
}

const RecentEntitiesContext = createContext<RecentEntitiesContextType | undefined>(undefined);

export const useRecentEntities = (): RecentEntitiesContextType => {
  const context = useContext(RecentEntitiesContext);
  if (!context) {
    throw new Error('useRecentEntities must be used within a RecentEntitiesProvider');
  }
  return context;
};

interface RecentEntitiesProviderProps {
  children: ReactNode;
}

export const RecentEntitiesProvider: React.FC<RecentEntitiesProviderProps> = ({ children }) => {
  const [recentEntities, setRecentEntities] = useState<RecentEntity[]>([]);

  const addRecentEntity = useCallback(
    (
      type: EntityType,
      data: ClientUserInterface | ClientBelief | ClientThoughtWithAuthor | ClientFeed
    ) => {
      const getId = (entity: any, entityType: EntityType): string => {
        switch (entityType) {
          case 'user':
            return entity.account_id?.toString();
          case 'belief':
            return entity.id?.toString();
          case 'thought':
            return entity.content_id?.toString();
          case 'feed':
            return entity.content_id?.toString();
          default:
            return '';
        }
      };

      const entityId = getId(data, type);
      if (!entityId) return;

      setRecentEntities((prev) => {
        // Remove existing entity with same id and type
        const filtered = prev.filter((entity) => !(entity.id === entityId && entity.type === type));

        // Add new entity at the beginning
        const newEntity: RecentEntity = {
          id: entityId,
          type,
          data,
          accessedAt: new Date(),
        };

        // Keep only the most recent MAX_RECENT_ENTITIES
        return [newEntity, ...filtered].slice(0, MAX_RECENT_ENTITIES);
      });
    },
    []
  );

  const value: RecentEntitiesContextType = {
    recentEntities,
    addRecentEntity,
  };

  return <RecentEntitiesContext.Provider value={value}>{children}</RecentEntitiesContext.Provider>;
};
